import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ChevronRight, MessageSquareReply, Star } from 'lucide-react';
import { GuestBottomNav } from '../../components/GuestBottomNav';
import { useBackTarget } from '../../hooks/useBackTarget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FONT = "'Inria Serif', serif";

const TABS = [
  { key: 'pending', label: 'Pending' },
  { key: 'history', label: 'History' },
];

export default function GuestRatingsPage() {
  const navigate = useNavigate();
  const goBack = useBackTarget();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get('submitted') ? 'history' : 'pending');
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [p, h] = await Promise.all([
          axios.get(`${API}/venue-reviews/all-pending`, { headers }),
          axios.get(`${API}/venue-reviews/history`, { headers }),
        ]);
        if (!active) return;
        setPending(p.data?.items || []);
        setHistory(h.data?.items || []);
      } catch (e) {
        // leave empty
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [headers]);

  return (
    <div className="app-shell" data-testid="guest-ratings-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '16px 18px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
        <button
          type="button"
          onClick={goBack}
          aria-label="Back"
          data-testid="guest-ratings-back"
          style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text)', padding: 6 }}
        >
          <ArrowLeft size={22} strokeWidth={2.2} />
        </button>
        <span style={{ fontFamily: FONT, fontSize: 20, fontWeight: 700, color: 'var(--text)' }}>Ratings & Reviews</span>
      </div>

      <div style={{ padding: '14px 18px 0', display: 'flex', gap: 8 }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          const badge = t.key === 'pending' ? pending.length : history.length;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`guest-ratings-tab-${t.key}`}
              style={{
                flex: 1,
                borderRadius: 9999,
                border: '1px solid ' + (active ? 'transparent' : 'rgba(112, 63, 246, 0.16)'),
                background: active ? 'var(--solid-cta-bg)' : 'var(--surface)',
                color: active ? 'var(--solid-cta-text)' : 'var(--text-2)',
                fontFamily: FONT,
                fontSize: 14,
                fontWeight: 700,
                padding: '10px 14px',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              {t.label}
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 9999,
                  background: active ? 'rgba(255,255,255,0.18)' : 'rgba(112, 63, 246, 0.10)',
                  color: active ? 'var(--solid-cta-text)' : 'var(--accent-purple)',
                  minWidth: 20,
                  textAlign: 'center',
                }}
              >
                {badge}
              </span>
            </button>
          );
        })}
      </div>

      <div className="page-content" style={{ padding: '16px 18px 110px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {loading ? (
          <div style={{ marginTop: 40, display: 'flex', justifyContent: 'center' }}>
            <div className="animate-spin" style={{ width: 36, height: 36, borderRadius: '50%', border: '4px solid rgba(112, 63, 246, 0.12)', borderTopColor: '#703FF6' }} />
          </div>
        ) : tab === 'pending' ? (
          pending.length === 0 ? (
            <EmptyState
              testId="guest-ratings-empty-pending"
              title="No pending ratings"
              body="When you pay at an Allure venue, you'll be able to rate it here for up to 5 days."
            />
          ) : (
            pending.map((p) => (
              <PendingRow key={p.transaction_id} prompt={p} onOpen={() => navigate(`/guest/rate/${p.transaction_id}`)} />
            ))
          )
        ) : history.length === 0 ? (
          <EmptyState
            testId="guest-ratings-empty-history"
            title="No reviews yet"
            body="Your published reviews will appear here once you start rating venues."
          />
        ) : (
          history.map((h) => <HistoryRow key={h.id} review={h} />)
        )}
      </div>

      <GuestBottomNav />
    </div>
  );
}

function PendingRow({ prompt, onOpen }) {
  const remainingDays = Math.max(1, Math.ceil((prompt.remaining_seconds || 0) / 86400));
  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid={`guest-ratings-pending-${prompt.transaction_id}`}
      style={{
        background: 'var(--surface)',
        border: '1px solid rgba(112, 63, 246, 0.10)',
        borderRadius: 16,
        padding: '14px 16px',
        boxShadow: '0 4px 7px rgba(112, 63, 246, 0.08)',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <span style={{ width: 36, height: 36, borderRadius: 12, background: 'rgba(244, 197, 78, 0.16)', color: '#D49B12', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Star size={18} fill="#F4C54E" color="#F4C54E" strokeWidth={2} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontFamily: FONT, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{prompt.venue_name}</p>
        <p style={{ margin: '2px 0 0', fontFamily: FONT, fontSize: 13, color: 'var(--text-2)' }}>
          €{Number(prompt.amount_paid).toFixed(2)} · {remainingDays} day{remainingDays > 1 ? 's' : ''} left to rate
        </p>
      </div>
      <ChevronRight size={18} color="#7A7590" />
    </button>
  );
}

function HistoryRow({ review }) {
  return (
    <div
      data-testid={`guest-ratings-history-${review.id}`}
      style={{
        background: 'var(--surface)',
        border: '1px solid rgba(112, 63, 246, 0.10)',
        borderRadius: 16,
        padding: '14px 16px',
        boxShadow: '0 4px 7px rgba(112, 63, 246, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontFamily: FONT, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>{review.venue_name}</span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: FONT, fontSize: 13, fontWeight: 700, color: '#D49B12' }}>
          <Star size={14} fill="#F4C54E" color="#F4C54E" /> {review.rating}/5
        </span>
      </div>
      {review.review_text ? (
        <p style={{ margin: 0, fontFamily: FONT, fontSize: 14, color: 'var(--text-2)', lineHeight: 1.4 }}>{review.review_text}</p>
      ) : null}
      {review.venue_reply?.text ? (
        <div style={{ marginTop: 4, padding: '10px 12px', background: 'rgba(112, 63, 246, 0.06)', borderRadius: 12, display: 'flex', gap: 8 }}>
          <MessageSquareReply size={15} color="#703FF6" />
          <div style={{ minWidth: 0 }}>
            <span style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#703FF6', textTransform: 'uppercase' }}>Reply from venue</span>
            <p style={{ margin: '2px 0 0', fontFamily: FONT, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.35 }}>{review.venue_reply.text}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function EmptyState({ testId, title, body }) {
  return (
    <div data-testid={testId} style={{ marginTop: 30, textAlign: 'center', padding: '20px 14px' }}>
      <p style={{ margin: 0, fontFamily: FONT, fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{title}</p>
      <p style={{ margin: '8px auto 0', fontFamily: FONT, fontSize: 14, color: 'var(--text-2)', maxWidth: 300, lineHeight: 1.4 }}>{body}</p>
    </div>
  );
}
